import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ArrowLeftRight, BatteryLow, Bell, Check, ChevronRight, Coffee, Flame, PartyPopper, Palmtree, Shuffle, Snowflake, StickyNote, Sun, Thermometer, Timer, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, Bar, Group, IconDisc, PageHeader, Row, RowText, Section, Spinner, Steps } from '@/components/kit';
import { Celebration } from '@/components/Celebration';
import { MachineTimers } from '@/components/MachineTimers';
import { RhythmCard } from '@/components/RhythmCard';
import { TimerDrawer } from '@/components/TimerDrawer';
import { RecapDrawer } from '@/components/RecapDrawer';
import { TradeCards, TradeDrawer } from '@/components/Trades';
import { NamedIcon } from '@/lib/icons';
import { api } from '@/lib/api';
import { choreName, formatDate, levelTitle, m, roomName } from '@/lib/i18n';
import { useAction } from '@/lib/queries';
import { dueText, lastDoneText } from '@/lib/chore-text';
import type { AppState, CompletionResult, DailyAssignment } from '@/lib/types';
import { cn } from '@/lib/utils';
import type { Tab } from '@/App';

export function Today({ state, go, openRecap }: { state: AppState; go: (tab: Tab, page?: string | null, focus?: string | null) => void; openRecap: boolean }) {
  const [result, setResult] = useState<CompletionResult | null>(null);
  const [timer, setTimer] = useState<number | null>(null);
  const [recapOpen, setRecapOpen] = useState(openRecap);
  const [tradeWith, setTradeWith] = useState<AppState['members'][number] | null>(null);
  const s = state;
  const iHavePending = s.dailies.some((d) => d.status === 'pending');
  const outgoing = s.trades.some((t) => !t.incoming);
  const pending = s.dailies.filter((d) => d.status === 'pending');
  const pendingBonus = s.bonuses.find((b) => b.status === 'pending');
  const allDone = s.dailies.length > 0 && pending.length === 0;
  const doneCount = s.dailies.filter((d) => d.status === 'done').length;

  const bonus = useAction(() => api('POST', '/today/bonus'));
  const endBreak = useAction(() => api('DELETE', '/me/pause'));

  return (
    <>
      <PageHeader title={m.tab_today()} sub={formatDate(Date.now(), { weekday: 'long', day: 'numeric', month: 'long' })} />
      <WeekStrip state={s} />

      {s.household.guestsSoon && s.household.guestsDate && (
        <p className="bg-accent text-accent-foreground -mt-2 flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-medium">
          <PartyPopper className="size-4 shrink-0" />
          {m.guests_banner({ date: formatDate(s.household.guestsDate, { weekday: 'long' }) })}
        </p>
      )}

      <TradeCards trades={s.trades} />

      <AnimatePresence mode="wait" initial={false}>
        {s.me.pause ? (
          <motion.section key="break" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }} className="bg-card grid gap-4 rounded-3xl border p-6">
            <div className="flex items-center gap-3">
              <IconDisc tone="accent">{s.me.pause.reason === 'sick' ? <Thermometer /> : <Palmtree />}</IconDisc>
              <div>
                <h2 className="text-xl font-bold">{s.me.pause.reason === 'sick' ? m.break_sick_title() : m.break_title()}</h2>
                <p className="text-muted-foreground text-sm">{m.break_until({ date: formatDate(s.me.pause.until, { weekday: 'long', day: 'numeric', month: 'long' }) })}</p>
              </div>
            </div>
            <p className="text-muted-foreground text-[15px]">{m.break_body()}</p>
            <Button variant="secondary" size="lg" onClick={() => endBreak.mutate()} disabled={endBreak.isPending}>
              {endBreak.isPending && <Spinner />}
              {m.break_end()}
            </Button>
          </motion.section>
        ) : s.dailyCount === 0 && !pendingBonus ? (
          <motion.section key="rest" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="bg-card grid justify-items-center gap-3 rounded-3xl border p-6 text-center">
            <div className="bg-accent text-accent-foreground grid size-16 place-items-center rounded-full">
              <Coffee className="size-7" />
            </div>
            <h2 className="text-xl font-bold">{m.rest_day_title()}</h2>
            <p className="text-muted-foreground text-sm">{m.rest_day_body()}</p>
            <BonusButton pending={bonus.isPending} onClick={() => bonus.mutate()} />
          </motion.section>
        ) : pending.length > 0 ? (
          pending.slice(0, 1).map((a) => (
            <ChoreCard
              key={a.id}
              a={a}
              label={s.dailies.length > 1 ? m.chore_n_of({ n: doneCount + 1, total: s.dailies.length }) : undefined}
              swapUsed={s.swapUsed}
              sky={s.sky}
              onDone={setResult}
              onTimer={setTimer}
            />
          ))
        ) : pendingBonus ? (
          <ChoreCard key={pendingBonus.id} a={pendingBonus} bonus swapUsed sky={s.sky} onDone={setResult} onTimer={setTimer} />
        ) : allDone ? (
          <motion.section key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-card grid justify-items-center gap-2.5 rounded-3xl border p-6 text-center">
            <motion.div initial={{ scale: 0.5 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 400, damping: 15 }} className="bg-accent text-accent-foreground grid size-16 place-items-center rounded-full">
              <Check className="size-8" strokeWidth={2.5} />
            </motion.div>
            <h2 className="text-xl font-bold">{m.done_for_today()}</h2>
            <p className="text-muted-foreground text-sm">
              {s.dailies.map((d) => choreName(d.chore)).join(' · ')}
              {s.bonuses.length > 0 && ` ${m.plus_bonus({ count: s.bonuses.length })}`}
            </p>
            <BonusButton pending={bonus.isPending} onClick={() => bonus.mutate()} />
          </motion.section>
        ) : (
          <motion.section key="none" className="bg-card text-muted-foreground rounded-3xl border p-6 text-center">
            {m.no_chores_available()}
          </motion.section>
        )}
      </AnimatePresence>

      {s.dailies.length > 1 && doneCount > 0 && pending.length > 0 && (
        <p className="text-muted-foreground -mt-4 flex items-center justify-center gap-1.5 text-sm">
          <Check className="text-primary size-4" /> {s.dailies.filter((d) => d.status === 'done').map((d) => choreName(d.chore)).join(' · ')}
        </p>
      )}

      <section className="grid gap-2 px-1">
        <div className="flex items-center justify-between text-sm">
          <span>
            <strong>{m.level_n({ level: s.me.level.level })}</strong> <span className="text-muted-foreground">· {levelTitle(s.me.level)}</span>
          </span>
          <span className="text-muted-foreground tabular-nums">
            {s.me.level.into} / {s.me.level.span} XP
          </span>
        </div>
        <Bar value={s.me.level.into / s.me.level.span} />
      </section>

      <MachineTimers state={s} />

      {s.rhythm[0] && <RhythmCard key={s.rhythm[0].choreId} tip={s.rhythm[0]} />}

      {s.members.length > 1 && (
        <Section title={m.household()}>
          <Group>
            {s.members.map((p) => (
              <Row key={p.id}>
                <Avatar name={p.name} color={p.avatar} photo={p.photo} />
                <RowText
                  title={
                    <>
                      {p.name} {p.isMe && <span className="text-muted-foreground">{m.you_suffix()}</span>}
                    </>
                  }
                  sub={<span className="block truncate">{p.paused ? m.on_a_break() : p.todayCount === 0 ? m.rest_day_title() : p.todayChore ? choreName(p.todayChore) : m.nothing_picked()}</span>}
                />
                {!p.isMe && p.todayPending && iHavePending && !outgoing && (
                  <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setTradeWith(p)}>
                    <ArrowLeftRight /> {m.trade()}
                  </Button>
                )}
                <span
                  aria-label={p.todayDone ? m.done() : m.not_done_yet()}
                  className={cn('grid size-8 shrink-0 place-items-center rounded-full border-[1.5px] transition-colors', p.todayDone ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/40 text-transparent')}
                >
                  <Check className="size-4" />
                </span>
              </Row>
            ))}
          </Group>
        </Section>
      )}

      {s.me.pushSubscriptions === 0 && (
        <Group>
          <Row onClick={() => go('profile', 'settings', 'reminders')}>
            <IconDisc>
              <Bell />
            </IconDisc>
            <RowText title={m.reminder_cta()} sub={m.reminder_cta_sub()} />
            <ChevronRight className="text-muted-foreground size-[18px]" />
          </Row>
        </Group>
      )}

      <Celebration result={result} onClose={() => setResult(null)} />
      <TimerDrawer minutes={timer} onClose={() => setTimer(null)} />
      <RecapDrawer state={s} open={recapOpen} onOpenChange={setRecapOpen} />
      <TradeDrawer state={s} with={tradeWith} onClose={() => setTradeWith(null)} />
    </>
  );
}

function BonusButton({ pending, onClick }: { pending: boolean; onClick: () => void }) {
  return (
    <Button variant="ghost" onClick={onClick} disabled={pending} className="text-muted-foreground">
      {pending ? <Spinner /> : <Zap />}
      {m.take_bonus()}
    </Button>
  );
}

function ChoreCard({
  a,
  bonus = false,
  label,
  swapUsed,
  sky,
  onDone,
  onTimer,
}: {
  a: DailyAssignment;
  bonus?: boolean;
  label?: string;
  swapUsed: boolean;
  sky?: string | null;
  onDone: (r: CompletionResult) => void;
  onTimer: (minutes: number) => void;
}) {
  const c = a.chore;
  const complete = useAction(() => api('POST', `/assignments/${a.id}/complete`), { onSuccess: (d) => onDone(d.result) });
  const swap = useAction((easy: boolean) => api('POST', '/today/swap', { easy }));
  return (
    <motion.section
      initial={{ opacity: 0, y: 16, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 60, scale: 0.96, transition: { duration: 0.2 } }}
      transition={{ type: 'spring', stiffness: 260, damping: 26 }}
      className="bg-card grid gap-5 rounded-3xl border p-6"
    >
      <div>
        <div className="text-muted-foreground flex items-center gap-1.5 text-sm font-medium">
          <NamedIcon name={c.roomIcon} className="size-4" />
          <span>
            {roomName(c.room)} · {m.minutes_short({ minutes: c.minutes })}
            {c.project && ` · ${m.big_job()}`}
            {bonus && ` · ${m.bonus()}`}
            {label && ` · ${label}`}
          </span>
        </div>
        <h2 className="mt-2 text-[26px] leading-tight font-bold tracking-tight text-balance">{choreName(c)}</h2>
        {c.project ? (
          <div className="mt-2 grid gap-1.5">
            <p className="text-muted-foreground text-sm">{m.project_step_of({ project: c.project.name, step: c.project.step, total: c.project.total })}</p>
            <Steps done={c.project.step - 1} total={c.project.total} />
          </div>
        ) : (
          <p className="text-muted-foreground mt-1.5 text-sm">
            {c.ratio > 1 && <span className="text-warn">{dueText(c)} · </span>}
            {lastDoneText(c)}
          </p>
        )}
        {c.outdoor && sky === 'dry' && (
          <p className="mt-3 flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-300">
            <Sun className="size-4" /> {m.dry_day()}
          </p>
        )}
        {c.notes && (
          <p className="bg-muted mt-3 flex gap-2 rounded-xl px-3 py-2.5 text-sm">
            <StickyNote className="text-muted-foreground mt-0.5 size-4 shrink-0" />
            <span className="whitespace-pre-line">{c.notes}</span>
          </p>
        )}
      </div>
      <div className="grid gap-1.5">
        <Button size="lg" className="h-14 justify-between px-6 text-base" onClick={() => complete.mutate()} disabled={complete.isPending} aria-busy={complete.isPending}>
          <span className="flex items-center gap-2">
            {complete.isPending && <Spinner />}
            {m.mark_done()}
          </span>
          <span className="text-sm opacity-80">+{a.xpPreview} XP</span>
        </Button>
        <div className="flex flex-wrap justify-center gap-x-1">
          {!bonus && (
            <>
              <Button variant="ghost" className="text-muted-foreground px-2.5" onClick={() => swap.mutate(false)} disabled={swapUsed || swap.isPending}>
                {swap.isPending && !swap.variables ? <Spinner /> : <Shuffle />}
                {swapUsed ? m.swapped() : m.swap()}
              </Button>
              {/* "Low energy today": the easiest chore that's due, using today's swap. */}
              {!swapUsed && (
                <Button variant="ghost" className="text-muted-foreground px-2.5" onClick={() => swap.mutate(true)} disabled={swap.isPending}>
                  {swap.isPending && swap.variables ? <Spinner /> : <BatteryLow />}
                  {m.easier_one()}
                </Button>
              )}
            </>
          )}
          <Button variant="ghost" className="text-muted-foreground px-2.5" onClick={() => onTimer(c.minutes)}>
            <Timer /> {m.timer()}
          </Button>
        </div>
      </div>
    </motion.section>
  );
}

function WeekStrip({ state }: { state: AppState }) {
  const { streak, freezes } = state.me;
  return (
    <section className="grid gap-2.5">
      <div className="grid grid-cols-7 gap-1 text-center">
        {state.week.map((d, i) => (
          <div key={d.date} className={cn('grid justify-items-center gap-1.5 text-xs font-semibold', d.today ? 'text-foreground' : 'text-muted-foreground')}>
            <span>{formatDate(d.date, { weekday: 'narrow' })}</span>
            <motion.span
              initial={false}
              animate={{ scale: d.done ? [1, 1.15, 1] : 1 }}
              transition={{ duration: 0.35, delay: i * 0.02 }}
              title={d.paused ? m.on_a_break() : d.rest ? m.rest_day_title() : undefined}
              className={cn(
                'grid size-[34px] place-items-center rounded-full border-[1.5px]',
                d.done && 'bg-primary border-primary text-primary-foreground',
                !d.done && d.today && 'border-primary border-2 border-dashed',
                !d.done && !d.today && (d.rest || d.paused) && 'bg-muted border-transparent',
                !d.done && !d.today && !d.rest && !d.paused && 'border-border',
              )}
            >
              {d.done ? <Check className="size-4" /> : d.paused ? <Palmtree className="size-3.5 opacity-60" /> : d.rest ? <Coffee className="size-3.5 opacity-60" /> : null}
            </motion.span>
          </div>
        ))}
      </div>
      <p className="text-muted-foreground flex items-center justify-center gap-1.5 text-sm">
        <Flame className="size-4" />
        {streak ? <strong className="text-foreground">{m.streak_days({ count: streak })}</strong> : m.no_streak()}
        {freezes > 0 && (
          <>
            <span>·</span>
            <Snowflake className="size-3.5" /> {m.freezes_count({ count: freezes })}
          </>
        )}
      </p>
    </section>
  );
}
